import { useCallback, useEffect, useState } from "react";

// LocalStorage-backed anonymous favorites — a simple Set of event IDs.
// Persisted under a versioned key so future migrations can bump it.
const KEY = "smq_favorites_v1";

function readStore() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeStore(set) {
  try {
    localStorage.setItem(KEY, JSON.stringify(Array.from(set)));
  } catch (e) {
    // Storage may be full, disabled (privacy mode), or blocked by CSP —
    // this is a UI-only feature, so log for debug and continue silently.
    if (typeof console !== "undefined") console.warn("[favorites] persist failed:", e?.message);
  }
}

// Cross-hook broadcast: keep every mounted useFavorites() in sync when one
// component toggles a favorite. Uses a plain event target on window.
const BUS = "smq-favorites-changed";

export default function useFavorites() {
  const [favorites, setFavorites] = useState(() => readStore());

  useEffect(() => {
    const onChange = () => setFavorites(readStore());
    window.addEventListener(BUS, onChange);
    window.addEventListener("storage", onChange); // cross-tab sync
    return () => {
      window.removeEventListener(BUS, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const toggle = useCallback((id) => {
    const next = readStore();
    if (next.has(id)) next.delete(id);
    else next.add(id);
    writeStore(next);
    setFavorites(next);
    window.dispatchEvent(new Event(BUS));
  }, []);

  const isFavorite = useCallback((id) => favorites.has(id), [favorites]);

  return { favorites, isFavorite, toggle, count: favorites.size };
}
